import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useRouteMatch, useLocation } from 'react-router-dom';
import 'moment/locale/fr';

import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Footer from '../../Components/Layout/Footer';
import { Images, muiTheme } from '../../Themes';
import LayoutStyles from '../../Components/Layout/Styles/LayoutStyles';
import Routes from '../../Routing/Routes';

import Seo from '../../Routing/Seo';
import SearchBar from '../../Components/Shared/Algolia/SearchBar';

import {
  CustomHitsOrganisation,
  CustomHitsTwig,
  // CustomHitsArticle,
} from '../../Components/Shared/Algolia/CustomHits';

import {
  INDEX_NAMES,
  FILTERING_ATTRIBUTES,
  SEARCH_BAR_SEARCH_ATTRIBUTES,
  LEAGUE_SLUG,
  GEOGRAPHIC_LIMITS,
  formatType,
  wordifyType,
} from '../../Services/AlgoliaHelper';
import InstantSearchResults from '../../Components/Shared/Algolia/InstantSearchResults';

const useStyles = makeStyles(theme => ({
  activityUnit: {
    paddingBottom: theme.spacing(3),
  },
  layout: {
    ...LayoutStyles.fullWidthLayout,
    paddingTop: '10px',
  },
  subHeroLayout: {
    // Make the picture goes under the navbar
    ...LayoutStyles.navbarPaddingTop,

    width: '100%',
    backgroundImage: `url(${Images.ccmormal.jumboSearchResults})`,
    backgroundSize: 'cover',
    backgroundPositionX: 'center',
    backgroundPositionY: '60%',
    padding: '3rem 0',
  },
  headingContainer: {
    width: '100%',
    padding: '3rem 0 3rem',
  },
  searchBar: {
    marginTop: 0,
  },
  results: {
    minHeight: '60vh',
  },
}));

function SearchResult() {
  const classes = useStyles();
  const match = useRouteMatch();
  const location = useLocation();
  const { t } = useTranslation();
  const {
    params: { type_slug: typeSlug },
  } = match;
  const type = formatType(typeSlug);
  const typeName = wordifyType(typeSlug);
  const queryParam = new URLSearchParams(location.search);
  const urlParams = {
    serviceSlug: queryParam.get('service') || '',
    city: queryParam.get('city') || '',
    address: queryParam.get('address') || '',
    latitude: queryParam.get('lat') || null,
    longitude: queryParam.get('long') || null,
    pathology: queryParam.get(FILTERING_ATTRIBUTES.ORGANISATION_PATHOLOGY_TYPE_ATTRIBUTE) || '',
  };
  const position = urlParams.latitude != null && urlParams.longitude != null;
  const filterName =
    // urlParams.pathology
    //   ? `${FILTERING_ATTRIBUTES.LEAGUE_ATTRIBUTE}:${LEAGUE_SLUG.CCMormal} AND ${FILTERING_ATTRIBUTES.ORGANISATION_PATHOLOGY_TYPE_ATTRIBUTE}:"${urlParams.pathology}"`
    //   :
    `${FILTERING_ATTRIBUTES.LEAGUE_ATTRIBUTE}:${LEAGUE_SLUG.CCMormal}`;
  const dateNow = Math.floor(Date.now() / 1000);

  const typeAttributes = {
    organisations: {
      indexName: INDEX_NAMES.ORGANISATION,
      title: 'APP.SEARCH_RESULT_PAGE.ORGANISATIONS',
      aroundCityPrefix: 'APP.SEARCH_RESULT_PAGE.CLOSE_TO',
      showCityInfo: true,
      showKeywordInfo: true,
      configureSettings: {
        hitsPerPage: 10,
        query: urlParams.serviceSlug,
        filters: filterName,
      },
      HitsDisplay: CustomHitsOrganisation,
      hasMap: true,
      refinementAttributes: [
        {
          label: t('APP.SEARCH_RESULT_PAGE.REFINEMENT.EXPERTISE'),
          attribute: FILTERING_ATTRIBUTES.ORGANISATION_SERVICE_EXPERTISE_SLUG_ATTRIBUTE,
        },
      ],
    },
    twigs: {
      indexName: INDEX_NAMES.TWIG,
      title: 'APP.SEARCH_RESULT_PAGE.TWIGS',
      aroundCityPrefix: 'APP.SEARCH_RESULT_PAGE.CLOSE_TO',
      showCityInfo: true,
      showKeywordInfo: true,
      configureSettings: {
        hitsPerPage: 12,
        query: urlParams.serviceSlug,
        filters: `${FILTERING_ATTRIBUTES.LEAGUE_ATTRIBUTE}:${LEAGUE_SLUG.CCMormal} AND ${FILTERING_ATTRIBUTES.TWIG_DATE_ATTRIBUTE} > ${dateNow}`,
      },
      HitsDisplay: CustomHitsTwig,
      hasMap: true,
      refinementAttributes: [
        {
          label: t('APP.SEARCH_RESULT_PAGE.REFINEMENT.EXPERTISE'),
          attribute: FILTERING_ATTRIBUTES.TWIG_EXPERTISE_SLUG_ATTRIBUTE,
          limit: 30,
          showMoreLimit: 30,
        },
        {
          label: t('APP.SEARCH_RESULT_PAGE.REFINEMENT.PATHOLOGY'),
          attribute: FILTERING_ATTRIBUTES.ORGANISATION_PATHOLOGY_TYPE_ATTRIBUTE,
        },
      ],
    },
    // articles: {
    //   indexName: INDEX_NAMES.ARTICLE,
    //   title: 'APP.SEARCH_RESULT_PAGE.ARTICLES',
    //   configureSettings: {
    //     hitsPerPage: 10,
    //     query: urlParams.serviceSlug,
    //     filters: `${FILTERING_ATTRIBUTES.LEAGUE_SLUG_ATTRIBUTE}:${LEAGUE_SLUG.Wello}`,
    //   },
    //   HitsDisplay: CustomHitsArticle,
    //   hasMap: false,
    //   showKeywordInfo: true,
    //   // refinementAttributes: [
    //   //   {
    //   //     label: t('APP.SEARCH_RESULT_PAGE.REFINEMENT.EXPERTISE'),
    //   //     attribute: FILTERING_ATTRIBUTES.ARTICLE_EXPERTISE_FACET_ATTRIBUTE,
    //   //   },
    //   // ],
    // },
  };

  return (
    <Grid>
      <Helmet>
        {urlParams.city &&
          ['organisations', 'twigs'].includes(type) &&
          Seo.title(
            `${typeName} à ${urlParams.city}${t(`${Routes.currentNamespace()}.SEO.SUFFIX`)}`,
          )}
        {(!urlParams.city || !['organisations', 'twigs'].includes(type)) &&
          Seo.title(`Résultats ${typeName}${t(`${Routes.currentNamespace()}.SEO.SUFFIX`)}`)}
        {Seo.canonical(
          Routes.currentLeague().id,
          `${location.pathname}${location.search.replaceAll(' ', '%20')}`,
        )}
      </Helmet>
      <Hidden only={['xs']}>
        <Grid container className={classes.container}>
          <Grid item className={classes.subHeroLayout}>
            <Grid item className={classes.layout} style={{ paddingTop: 0 }}>
              <SearchBar
                className={classes.searchBar}
                service={urlParams.serviceSlug}
                address={urlParams.address}
                city={urlParams.city}
                latitude={urlParams.latitude}
                longitude={urlParams.longitude}
                type={type}
                goBackgrounColor={muiTheme.ccmormal.primary.main}
                searchAttributes={SEARCH_BAR_SEARCH_ATTRIBUTES.CCMormal}
                defaultType="structures"
                // showPathologiesFilter
                pathologyParam={urlParams.pathology}
                geographicLimits={GEOGRAPHIC_LIMITS.Mormal}
              />
            </Grid>
          </Grid>
        </Grid>
      </Hidden>

      <Grid className={classes.results}>
        <InstantSearchResults
          request={typeAttributes[type]}
          position={position ? { lat: urlParams.latitude, lng: urlParams.longitude } : null}
          typeAttributes={typeAttributes}
          tabs={[
            { label: t('APP.SEARCH_RESULT_PAGE.TAB.ORGANISATIONS'), value: 'organisations' },
            { label: t('APP.SEARCH_RESULT_PAGE.TAB.TWIGS'), value: 'twigs' },
            // { label: t('APP.SEARCH_RESULT_PAGE.TAB.ARTICLES'), value: 'articles' },
          ]}
        />
      </Grid>
      <Footer />
    </Grid>
  );
}

export default SearchResult;
